import {
  UserTrait,
  UserTraitTreeNode,
  TraitHierarchy,
} from "../../../api/userTraits";

// ─── Assessment Levels ──────────────────────────────────────────────────────

export const ASSESSMENT_LEVELS = [
  {
    value: 0,
    key: "aspiration",
    color: "text-gray-400",
    bgColor: "bg-gray-100",
    ringColor: "ring-gray-300",
  },
  {
    value: 1,
    key: "emerging",
    color: "text-blue-500",
    bgColor: "bg-blue-50",
    ringColor: "ring-blue-300",
  },
  {
    value: 2,
    key: "developing",
    color: "text-amber-500",
    bgColor: "bg-amber-50",
    ringColor: "ring-amber-300",
  },
  {
    value: 3,
    key: "embodied",
    color: "text-emerald-500",
    bgColor: "bg-emerald-50",
    ringColor: "ring-emerald-300",
  },
] as const;

export const HIERARCHY_ICONS = {
  domain: "Layers",
  capability: "GitBranch",
  behavior: "Target",
} as const;

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Extract a localized name from a { en, de, he } object.
 * Falls back to `en` then to the first available value.
 */
export function getLocalizedName(
  nameObj: any,
  lang: string
): string {
  if (!nameObj) return "";
  if (typeof nameObj === "string") return nameObj;
  return nameObj[lang] || nameObj["en"] || Object.values(nameObj).find((v: any) => !!v) || "";
}

/**
 * Get the full config for an assessment level (0-3).
 */
export function getLevelConfig(level: number | undefined | null) {
  if (level === undefined || level === null || isNaN(level as number)) {
    return ASSESSMENT_LEVELS[0];
  }
  const clamped = Math.max(0, Math.min(3, Math.round(level)));
  return ASSESSMENT_LEVELS[clamped];
}

/**
 * Get the translated label for an assessment level.
 */
export function getLevelLabel(level: number, t: Function): string {
  const config = getLevelConfig(level);
  return t(`trait_level_${config.key}`, config.key);
}

/**
 * Get the translated label for a hierarchy level.
 */
export function getHierarchyLabel(
  hierarchy: string | null | undefined,
  t: Function
): string {
  if (!hierarchy) return "";
  const labels: Record<string, string> = {
    domain: t("trait_hierarchy_domain", "Domain"),
    capability: t("trait_hierarchy_capability", "Capability"),
    behavior: t("trait_hierarchy_behavior", "Behavior"),
  };
  return labels[hierarchy] || hierarchy;
}

/**
 * Build a nested tree from a flat array of UserTraits.
 * Traits without a parentTraitId are root nodes.
 */
export function buildTraitTree(
  flatTraits: UserTrait[]
): UserTraitTreeNode[] {
  const map = new Map<string, UserTraitTreeNode>();
  const roots: UserTraitTreeNode[] = [];

  // First pass: create nodes
  for (let i = 0; i < flatTraits.length; i++) {
    const trait = flatTraits[i];
    map.set(trait._id, { ...trait, children: [] } as UserTraitTreeNode);
  }

  // Second pass: link parents
  for (let i = 0; i < flatTraits.length; i++) {
    const trait = flatTraits[i];
    const node = map.get(trait._id)!;
    if (trait.parentTraitId && map.has(trait.parentTraitId)) {
      map.get(trait.parentTraitId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

/**
 * Flatten a tree back into a flat array (depth-first).
 */
export function flattenTraitTree(
  nodes: UserTraitTreeNode[]
): UserTrait[] {
  const result: UserTrait[] = [];

  function walk(list: UserTraitTreeNode[]) {
    for (let i = 0; i < list.length; i++) {
      const node = list[i];
      const { children, ...rest } = node;
      const safeChildren = children ?? [];
      result.push({
        ...rest,
        children: safeChildren.map((c) => c._id),
      } as UserTrait);
      if (safeChildren.length > 0) {
        walk(safeChildren);
      }
    }
  }

  walk(nodes);
  return result;
}

/**
 * Count total evidence entries for a trait, including its children
 * (direct evidence on the trait + recursive child evidence).
 */
export function countEvidence(trait: UserTrait | UserTraitTreeNode): number {
  let count = trait.evidence ? trait.evidence.length : 0;
  // If the trait has a totalEvidence computed field from the server, prefer that
  if (typeof (trait as any).totalEvidence === "number") {
    return (trait as any).totalEvidence;
  }
  // If children are tree nodes (objects), recurse
  if (
    Array.isArray(trait.children) &&
    trait.children.length > 0 &&
    typeof trait.children[0] === "object"
  ) {
    const childNodes = trait.children as unknown as UserTraitTreeNode[];
    for (let i = 0; i < childNodes.length; i++) {
      count += countEvidence(childNodes[i]);
    }
  }
  return count;
}
